import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { useAuth } from "./AuthContext";

const translations = {
  en: {
    // Nav
    "nav.dashboard": "Dashboard",
    "nav.projects": "Projects",
    "nav.tasks": "Tasks",
    "nav.jobs": "Jobs",
    "nav.suppliers": "Suppliers",
    "nav.clients": "Clients",
    "nav.messaging": "Messaging",
    "nav.work_orders": "Work Orders",
    "nav.invoices": "Invoices",
    "nav.reports": "Reports",
    "nav.settings": "Settings",
    "nav.emergency": "Emergency Jobs",
    "nav.knowledge_base": "Knowledge Base",
    "nav.analytics": "Analytics",
    "nav.tenants": "Tenants",
    "nav.revenue": "Revenue",
    "nav.billing": "Billing",
    "nav.branding": "Branding",
    "nav.webhooks": "Webhooks",
    "nav.email_templates": "Email Templates",
    // Common
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.create": "Create",
    "common.search": "Search",
    "common.filter": "Filter",
    "common.loading": "Loading...",
    "common.no_data": "No data available",
    "common.back": "Back",
    "common.next": "Next",
    "common.submit": "Submit",
    "common.close": "Close",
    "common.confirm": "Confirm",
    "common.export": "Export",
    "common.import": "Import",
    "common.download": "Download",
    "common.upload": "Upload",
    "common.status": "Status",
    "common.actions": "Actions",
    "common.name": "Name",
    "common.email": "Email",
    "common.role": "Role",
    "common.active": "Active",
    "common.inactive": "Inactive",
    "common.all": "All",
    // Dashboard
    "dashboard.welcome": "Welcome back",
    "dashboard.overview": "Here's what's happening today",
    "dashboard.total_projects": "Total Projects",
    "dashboard.active_tasks": "Active Tasks",
    "dashboard.overdue": "Overdue",
    "dashboard.pending_review": "Pending Review",
    // Auth
    "auth.login": "Sign In",
    "auth.logout": "Sign Out",
    "auth.register": "Create Account",
    "auth.forgot_password": "Forgot Password?",
    "auth.verify_email": "Please verify your email address",
    "auth.resend": "Resend Email",
    // Onboarding
    "onboarding.title": "Getting Started",
    "onboarding.subtitle": "Complete these steps to set up your workspace",
    "onboarding.complete": "All done! Your workspace is fully set up.",
    "onboarding.dismiss": "Dismiss",
    // Settings
    "settings.language": "Language",
    "settings.language_desc": "Choose your preferred language",
    "settings.data_management": "Data Management",
    "settings.export_data": "Export Data",
    "settings.import_data": "Import Data",
  },
  es: {
    "nav.dashboard": "Panel",
    "nav.projects": "Proyectos",
    "nav.tasks": "Tareas",
    "nav.jobs": "Trabajos",
    "nav.suppliers": "Proveedores",
    "nav.clients": "Clientes",
    "nav.messaging": "Mensajes",
    "nav.work_orders": "Ordenes de Trabajo",
    "nav.invoices": "Facturas",
    "nav.reports": "Informes",
    "nav.settings": "Configuracion",
    "nav.emergency": "Emergencias",
    "nav.knowledge_base": "Base de Conocimiento",
    "nav.analytics": "Analiticas",
    "nav.tenants": "Inquilinos",
    "nav.revenue": "Ingresos",
    "nav.billing": "Facturacion",
    "nav.branding": "Marca",
    "nav.webhooks": "Webhooks",
    "nav.email_templates": "Plantillas de Email",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.create": "Crear",
    "common.search": "Buscar",
    "common.filter": "Filtrar",
    "common.loading": "Cargando...",
    "common.no_data": "Sin datos disponibles",
    "common.back": "Atras",
    "common.next": "Siguiente",
    "common.submit": "Enviar",
    "common.close": "Cerrar",
    "common.confirm": "Confirmar",
    "common.export": "Exportar",
    "common.import": "Importar",
    "common.download": "Descargar",
    "common.upload": "Subir",
    "common.status": "Estado",
    "common.actions": "Acciones",
    "common.name": "Nombre",
    "common.email": "Correo",
    "common.role": "Rol",
    "common.active": "Activo",
    "common.inactive": "Inactivo",
    "common.all": "Todos",
    "dashboard.welcome": "Bienvenido de nuevo",
    "dashboard.overview": "Esto es lo que esta pasando hoy",
    "dashboard.total_projects": "Proyectos Totales",
    "dashboard.active_tasks": "Tareas Activas",
    "dashboard.overdue": "Atrasados",
    "dashboard.pending_review": "Revision Pendiente",
    "auth.login": "Iniciar Sesion",
    "auth.logout": "Cerrar Sesion",
    "auth.register": "Crear Cuenta",
    "auth.forgot_password": "Olvidaste tu contrasena?",
    "auth.verify_email": "Por favor verifica tu correo electronico",
    "auth.resend": "Reenviar Email",
    "onboarding.title": "Primeros Pasos",
    "onboarding.subtitle": "Completa estos pasos para configurar tu espacio",
    "onboarding.complete": "Listo! Tu espacio esta completamente configurado.",
    "onboarding.dismiss": "Descartar",
    "settings.language": "Idioma",
    "settings.language_desc": "Elige tu idioma preferido",
    "settings.data_management": "Gestion de Datos",
    "settings.export_data": "Exportar Datos",
    "settings.import_data": "Importar Datos",
  },
  fr: {
    "nav.dashboard": "Tableau de bord",
    "nav.projects": "Projets",
    "nav.tasks": "Taches",
    "nav.jobs": "Emplois",
    "nav.suppliers": "Fournisseurs",
    "nav.clients": "Clients",
    "nav.messaging": "Messagerie",
    "nav.work_orders": "Bons de travail",
    "nav.invoices": "Factures",
    "nav.reports": "Rapports",
    "nav.settings": "Parametres",
    "nav.emergency": "Urgences",
    "nav.knowledge_base": "Base de connaissances",
    "nav.analytics": "Analytique",
    "nav.tenants": "Locataires",
    "nav.revenue": "Revenus",
    "nav.billing": "Facturation",
    "nav.branding": "Marque",
    "nav.webhooks": "Webhooks",
    "nav.email_templates": "Modeles d'email",
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",
    "common.delete": "Supprimer",
    "common.edit": "Modifier",
    "common.create": "Creer",
    "common.search": "Rechercher",
    "common.filter": "Filtrer",
    "common.loading": "Chargement...",
    "common.no_data": "Aucune donnee disponible",
    "common.back": "Retour",
    "common.next": "Suivant",
    "common.submit": "Soumettre",
    "common.close": "Fermer",
    "common.confirm": "Confirmer",
    "common.export": "Exporter",
    "common.import": "Importer",
    "common.download": "Telecharger",
    "common.upload": "Telecharger",
    "common.status": "Statut",
    "common.actions": "Actions",
    "common.name": "Nom",
    "common.email": "Email",
    "common.role": "Role",
    "common.active": "Actif",
    "common.inactive": "Inactif",
    "common.all": "Tous",
    "dashboard.welcome": "Bienvenue",
    "dashboard.overview": "Voici ce qui se passe aujourd'hui",
    "dashboard.total_projects": "Projets Totaux",
    "dashboard.active_tasks": "Taches Actives",
    "dashboard.overdue": "En retard",
    "dashboard.pending_review": "En attente de revision",
    "auth.login": "Se connecter",
    "auth.logout": "Se deconnecter",
    "auth.register": "Creer un compte",
    "auth.forgot_password": "Mot de passe oublie?",
    "auth.verify_email": "Veuillez verifier votre adresse email",
    "auth.resend": "Renvoyer l'email",
    "onboarding.title": "Pour commencer",
    "onboarding.subtitle": "Completez ces etapes pour configurer votre espace",
    "onboarding.complete": "Termine! Votre espace est entierement configure.",
    "onboarding.dismiss": "Ignorer",
    "settings.language": "Langue",
    "settings.language_desc": "Choisissez votre langue preferee",
    "settings.data_management": "Gestion des donnees",
    "settings.export_data": "Exporter les donnees",
    "settings.import_data": "Importer les donnees",
  },
  de: {
    "nav.dashboard": "Ubersicht",
    "nav.projects": "Projekte",
    "nav.tasks": "Aufgaben",
    "nav.jobs": "Auftrage",
    "nav.suppliers": "Lieferanten",
    "nav.clients": "Kunden",
    "nav.messaging": "Nachrichten",
    "nav.work_orders": "Arbeitsauftrage",
    "nav.invoices": "Rechnungen",
    "nav.reports": "Berichte",
    "nav.settings": "Einstellungen",
    "nav.emergency": "Notfalle",
    "nav.knowledge_base": "Wissensdatenbank",
    "nav.analytics": "Analysen",
    "nav.tenants": "Mandanten",
    "nav.revenue": "Umsatz",
    "nav.billing": "Abrechnung",
    "nav.branding": "Branding",
    "nav.webhooks": "Webhooks",
    "nav.email_templates": "E-Mail-Vorlagen",
    "common.save": "Speichern",
    "common.cancel": "Abbrechen",
    "common.delete": "Loschen",
    "common.edit": "Bearbeiten",
    "common.create": "Erstellen",
    "common.search": "Suchen",
    "common.filter": "Filtern",
    "common.loading": "Laden...",
    "common.no_data": "Keine Daten verfugbar",
    "common.back": "Zuruck",
    "common.next": "Weiter",
    "common.submit": "Absenden",
    "common.close": "Schliessen",
    "common.confirm": "Bestatigen",
    "common.export": "Exportieren",
    "common.import": "Importieren",
    "common.download": "Herunterladen",
    "common.upload": "Hochladen",
    "common.status": "Status",
    "common.actions": "Aktionen",
    "common.name": "Name",
    "common.email": "E-Mail",
    "common.role": "Rolle",
    "common.active": "Aktiv",
    "common.inactive": "Inaktiv",
    "common.all": "Alle",
    "dashboard.welcome": "Willkommen zuruck",
    "dashboard.overview": "Hier ist was heute passiert",
    "dashboard.total_projects": "Projekte Gesamt",
    "dashboard.active_tasks": "Aktive Aufgaben",
    "dashboard.overdue": "Uberfalling",
    "dashboard.pending_review": "Uberprufung ausstehend",
    "auth.login": "Anmelden",
    "auth.logout": "Abmelden",
    "auth.register": "Konto erstellen",
    "auth.forgot_password": "Passwort vergessen?",
    "auth.verify_email": "Bitte bestatigen Sie Ihre E-Mail-Adresse",
    "auth.resend": "E-Mail erneut senden",
    "onboarding.title": "Erste Schritte",
    "onboarding.subtitle": "Fuhren Sie diese Schritte aus um Ihren Arbeitsbereich einzurichten",
    "onboarding.complete": "Fertig! Ihr Arbeitsbereich ist vollstandig eingerichtet.",
    "onboarding.dismiss": "Verwerfen",
    "settings.language": "Sprache",
    "settings.language_desc": "Wahlen Sie Ihre bevorzugte Sprache",
    "settings.data_management": "Datenverwaltung",
    "settings.export_data": "Daten exportieren",
    "settings.import_data": "Daten importieren",
  },
  pt: {
    "nav.dashboard": "Painel",
    "nav.projects": "Projetos",
    "nav.tasks": "Tarefas",
    "nav.jobs": "Trabalhos",
    "nav.suppliers": "Fornecedores",
    "nav.clients": "Clientes",
    "nav.messaging": "Mensagens",
    "nav.work_orders": "Ordens de Servico",
    "nav.invoices": "Faturas",
    "nav.reports": "Relatorios",
    "nav.settings": "Configuracoes",
    "nav.emergency": "Emergencias",
    "nav.knowledge_base": "Base de Conhecimento",
    "nav.analytics": "Analises",
    "nav.tenants": "Inquilinos",
    "nav.revenue": "Receita",
    "nav.billing": "Cobranca",
    "nav.branding": "Marca",
    "nav.webhooks": "Webhooks",
    "nav.email_templates": "Modelos de Email",
    "common.save": "Salvar",
    "common.cancel": "Cancelar",
    "common.delete": "Excluir",
    "common.edit": "Editar",
    "common.create": "Criar",
    "common.search": "Buscar",
    "common.filter": "Filtrar",
    "common.loading": "Carregando...",
    "common.no_data": "Sem dados disponiveis",
    "common.back": "Voltar",
    "common.next": "Proximo",
    "common.submit": "Enviar",
    "common.close": "Fechar",
    "common.confirm": "Confirmar",
    "common.export": "Exportar",
    "common.import": "Importar",
    "common.download": "Baixar",
    "common.upload": "Enviar",
    "common.status": "Status",
    "common.actions": "Acoes",
    "common.name": "Nome",
    "common.email": "Email",
    "common.role": "Funcao",
    "common.active": "Ativo",
    "common.inactive": "Inativo",
    "common.all": "Todos",
    "dashboard.welcome": "Bem-vindo de volta",
    "dashboard.overview": "Veja o que esta acontecendo hoje",
    "dashboard.total_projects": "Total de Projetos",
    "dashboard.active_tasks": "Tarefas Ativas",
    "dashboard.overdue": "Atrasados",
    "dashboard.pending_review": "Revisao Pendente",
    "auth.login": "Entrar",
    "auth.logout": "Sair",
    "auth.register": "Criar Conta",
    "auth.forgot_password": "Esqueceu a senha?",
    "auth.verify_email": "Por favor verifique seu endereco de email",
    "auth.resend": "Reenviar Email",
    "onboarding.title": "Primeiros Passos",
    "onboarding.subtitle": "Complete estes passos para configurar seu espaco",
    "onboarding.complete": "Pronto! Seu espaco esta totalmente configurado.",
    "onboarding.dismiss": "Dispensar",
    "settings.language": "Idioma",
    "settings.language_desc": "Escolha seu idioma preferido",
    "settings.data_management": "Gerenciamento de Dados",
    "settings.export_data": "Exportar Dados",
    "settings.import_data": "Importar Dados",
  },
};

export const LANGUAGES = [
  { code: "en", name: "English", flag: "GB" },
  { code: "es", name: "Espanol", flag: "ES" },
  { code: "fr", name: "Francais", flag: "FR" },
  { code: "de", name: "Deutsch", flag: "DE" },
  { code: "pt", name: "Portugues", flag: "PT" },
];

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem("necos_language") || "en";
  });

  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguageState(user.language);
      localStorage.setItem("necos_language", user.language);
    }
  }, [user?.language]);

  const setLanguage = useCallback(async (lang) => {
    setLanguageState(lang);
    localStorage.setItem("necos_language", lang);
    try {
      await API.put("/users/me/language", { language: lang });
    } catch {}
  }, []);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.en?.[key] || key;
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
